import React, { useEffect, useState } from 'react';
import * as LuIcon from 'react-icons/lu';
import * as Icon from 'react-bootstrap-icons';
import { Collapse, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import Skeleton from 'react-loading-skeleton';

import { GetStageDefinitionWithStatusDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../translations';
import {
  TimelineStageColor,
  TimelineStageEnum,
  TimelineStageObj,
  TimelineStageStatusEnum,
  TimelineStageStatusObj,
} from '../../../common/pokCore/validation/enums';

import { TimelineStages } from './TimelineStages';
import { StageModal } from './StageModal';

const iconMap: Record<TimelineStageEnum, Icon.Icon> = {
  [TimelineStageEnum.CampaignCompletion]: LuIcon.LuSparkles,
  [TimelineStageEnum.Contracts]: LuIcon.LuClipboardList,
  [TimelineStageEnum.IntercompanyOrders]: LuIcon.LuBuilding2,
  [TimelineStageEnum.CampaignSettlement]: LuIcon.LuCalculator,
};

export interface Stage {
  id: string;
  icon: Icon.Icon;
  label: string;
  color: TimelineStageColor;
  tooltip?: string;
}

interface TimeLineProps {
  stages: GetStageDefinitionWithStatusDto[];
  startDate: Date;
  endDate: Date;
  onMonthChange?: (month: string | undefined) => void;
  projectId: string;
  isLoading?: boolean;
  onRefresh: () => void;
}

export const TimeLine: React.FC<TimeLineProps> = ({
  stages,
  startDate,
  endDate,
  onMonthChange,
  projectId,
  isLoading,
  onRefresh,
}) => {
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>(
    undefined,
  );
  const [monthOptions, setMonthOptions] = useState<
    { value: string | undefined; label: string }[]
  >([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<Stage | null>(null);
  const [open, setOpen] = useState(true);

  const { t, tk } = useTranslation('projectLng');

  useEffect(() => {
    const start = moment(startDate).startOf('month');
    const end = moment(endDate).startOf('month');
    const current = start.clone();

    const monthsList: { value: string | undefined; label: string }[] = [];

    while (current.isSameOrBefore(end)) {
      monthsList.push({
        value: current.format('YYYY-MM-DD'),
        label: current.format('MM.YYYY'),
      });
      current.add(1, 'month');
    }

    const options: { value: string | undefined; label: string }[] =
      monthsList.length > 1
        ? [{ value: undefined, label: t(tk.timeLine.all) }, ...monthsList]
        : monthsList;

    setMonthOptions(options);
    setSelectedMonth(monthsList.length === 1 ? monthsList[0].value : undefined);
  }, [startDate, endDate, t, tk.timeLine.all]);

  const stagesWithColor: Stage[] = stages.map(stage => {
    let color: TimelineStageColor = TimelineStageColor.Black;
    if (stage.status === TimelineStageStatusEnum.Completed) {
      color = TimelineStageColor.Teal;
    } else if (stage.status === TimelineStageStatusEnum.InProgress) {
      color = TimelineStageColor.Blue;
    } else if (stage.status === TimelineStageStatusEnum.Error) {
      color = TimelineStageColor.Red;
    } else if (stage.status === TimelineStageStatusEnum.NotApplicable) {
      color = TimelineStageColor.Gray;
    }
    return {
      id: stage.id,
      icon:
        iconMap[stage.stageType as keyof typeof iconMap] || LuIcon.LuSparkles,
      label: t(TimelineStageObj[stage.stageType]),
      color: color,
      tooltip: `${t(TimelineStageObj[stage.stageType])} - ${t(TimelineStageStatusObj[stage.status as keyof typeof TimelineStageStatusObj] ?? tk.enum.notStarted)}`,
    };
  });

  const openModal = (stage: Stage) => {
    setSelectedStage(stage);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedStage(null);
  };

  return (
    <>
      <div className="border rounded-3" id="timeline-stage-container">
        <div className="d-flex justify-content-between align-items-center p-1 rounded-top">
          <span className="w-100 text-center timeline-header-text">
            {!open && t(tk.timeLine.title)}
          </span>
          <div className="d-flex align-items-center gap-1">
            <OverlayTrigger
              overlay={<Tooltip>{t(tk.timeLine.refresh)}</Tooltip>}
            >
              <button
                className="btn btn-sm btn-outline-primary d-flex align-items-center"
                onClick={onRefresh}
                disabled={isLoading}
              >
                <Icon.ArrowClockwise size={16} />
              </button>
            </OverlayTrigger>
            <OverlayTrigger
              overlay={
                <Tooltip>
                  {open ? t(tk.timeLine.collapse) : t(tk.timeLine.expand)}
                </Tooltip>
              }
            >
              <button
                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 timeline-collapse-button"
                onClick={() => setOpen(!open)}
              >
                <span className="timeline-collapse-icon">
                  {open ? '▲' : '▼'}
                </span>
              </button>
            </OverlayTrigger>
          </div>
        </div>

        <Collapse in={open}>
          <div>
            <div className="p-4">
              <div className="d-flex align-items-center w-100 timeline-content-wrapper">
                <div className="d-flex align-items-center flex-shrink-0 ms-2 timeline-filter-section">
                  <label className="form-label mb-0 text-nowrap timeline-filter-label">
                    {t(tk.timeLine.month)}
                  </label>
                  <div className="timeline-select-container">
                    <Select
                      value={monthOptions.find(
                        ({ value }) => value === selectedMonth,
                      )}
                      options={monthOptions}
                      onChange={option => {
                        const newMonth = option?.value;
                        setSelectedMonth(newMonth);
                        onMonthChange?.(newMonth);
                      }}
                    />
                  </div>
                </div>

                <div className="flex-fill d-flex align-items-center">
                  {isLoading ? (
                    <div className="w-100">
                      <Skeleton height={116} borderRadius={0.375} />
                    </div>
                  ) : (
                    <TimelineStages
                      stages={stagesWithColor}
                      stageClicked={openModal}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </Collapse>
      </div>
      {selectedStage && (
        <StageModal
          stage={selectedStage}
          show={modalOpen}
          onClose={closeModal}
          projectId={projectId}
          monthsOptions={monthOptions}
          selectedMonth={selectedMonth}
        />
      )}
    </>
  );
};
